import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "components/AppIcon";
import { useAuth, ROLE_ROUTES } from "../../../contexts/AuthContext";

function PasswordStrength({ password }) {
  const getStrength = (pwd) => {
    let score = 0;
    if (pwd?.length >= 8) score++;
    if (/[A-Z]/?.test(pwd)) score++;
    if (/[0-9]/?.test(pwd)) score++;
    if (/[^A-Za-z0-9]/?.test(pwd)) score++;
    return score;
  };

  const strength = getStrength(password);
  const labels = ["", "Weak", "Fair", "Good", "Strong"];
  const colors = ["", "bg-red-500", "bg-amber-500", "bg-yellow-400", "bg-emerald-500"];
  const textColors = ["", "text-red-400", "text-amber-400", "text-yellow-400", "text-emerald-400"];

  if (!password) return null;

  return (
    <div className="mt-2 space-y-1.5">
      <div className="flex gap-1">
        {[1, 2, 3, 4]?.map((i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full transition-all duration-300 ${
              i <= strength ? colors?.[strength] : "bg-white/10"
            }`}
          />
        ))}
      </div>
      {strength > 0 && (
        <p className={`text-xs font-caption ${textColors?.[strength]}`}>
          {labels?.[strength]} password
        </p>
      )}
    </div>
  );
}

export default function RegistrationForm({ onShowTerms }) {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [formData, setFormData] = useState({
    ownerName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    agreeTerms: false,
    agreePrivacy: false,
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState("");

  const validate = () => {
    const errs = {};
    if (!formData?.ownerName?.trim()) errs.ownerName = "Owner name is required";
    if (!formData?.email) errs.email = "Email is required";
    else if (!/\S+@\S+\.\S+/?.test(formData?.email)) errs.email = "Enter a valid email";
    if (!formData?.phone) errs.phone = "Phone number is required";
    else if (!/^[\d\s\-\+\(\)]{10,}$/?.test(formData?.phone)) errs.phone = "Enter a valid phone number";
    if (!formData?.password) errs.password = "Password is required";
    else if (formData?.password?.length < 8) errs.password = "Password must be at least 8 characters";
    if (!formData?.confirmPassword) errs.confirmPassword = "Please confirm your password";
    else if (formData?.password !== formData?.confirmPassword) errs.confirmPassword = "Passwords do not match";
    if (!formData?.agreeTerms) errs.agreeTerms = "You must accept the Terms & Conditions";
    if (!formData?.agreePrivacy) errs.agreePrivacy = "You must accept the Privacy Policy";
    return errs;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    if (errors?.[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    if (authError) setAuthError("");
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const errs = validate();
    if (Object.keys(errs)?.length > 0) { setErrors(errs); return; }
    setIsLoading(true);
    setAuthError("");

    const { data, error } = await signUp({
      email: formData?.email,
      password: formData?.password,
      fullName: formData?.ownerName,
      phone: formData?.phone,
      role: 'vendor',
    });

    setIsLoading(false);

    if (error) {
      setAuthError(error?.message || "Registration failed. Please try again.");
      return;
    }

    // Redirect new vendor to gym setup before portal
    navigate('/gym-setup');
  };

  const fieldClass = (name) =>
    `w-full h-11 pl-10 pr-4 rounded-xl bg-white/5 border text-white placeholder-slate-500 text-sm font-body focus:outline-none focus:ring-2 focus:ring-teal-500/50 transition-all ${
      errors?.[name] ? "border-red-500/60" : "border-white/10 focus:border-teal-500/50"
    }`;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {authError && (
        <div className="flex items-center gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20">
          <Icon name="AlertCircle" size={16} color="#f87171" />
          <p className="text-xs text-red-400 font-caption">{authError}</p>
        </div>
      )}
      {/* Owner Name */}
      <div className="space-y-1.5">
        <label className="text-sm font-medium text-slate-300 font-caption">Owner Name *</label>
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
            <Icon name="User" size={16} />
          </div>
          <input
            type="text"
            name="ownerName"
            value={formData?.ownerName}
            onChange={handleChange}
            placeholder="John Smith"
            className={fieldClass("ownerName")}
          />
        </div>
        {errors?.ownerName && <p className="text-xs text-red-400 font-caption">{errors?.ownerName}</p>}
      </div>
      {/* Row 2: Email + Phone */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-slate-300 font-caption">Email Address *</label>
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
              <Icon name="Mail" size={16} />
            </div>
            <input
              type="email"
              name="email"
              value={formData?.email}
              onChange={handleChange}
              placeholder="owner@gym.com"
              className={fieldClass("email")}
            />
          </div>
          {errors?.email && <p className="text-xs text-red-400 font-caption">{errors?.email}</p>}
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-medium text-slate-300 font-caption">Phone Number *</label>
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
              <Icon name="Phone" size={16} />
            </div>
            <input
              type="tel"
              name="phone"
              value={formData?.phone}
              onChange={handleChange}
              placeholder="+1 (555) 000-0000"
              className={fieldClass("phone")}
            />
          </div>
          {errors?.phone && <p className="text-xs text-red-400 font-caption">{errors?.phone}</p>}
        </div>
      </div>
      {/* Password */}
      <div className="space-y-1.5">
        <label className="text-sm font-medium text-slate-300 font-caption">Password *</label>
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
            <Icon name="Lock" size={16} />
          </div>
          <input
            type={showPassword ? "text" : "password"}
            name="password"
            value={formData?.password}
            onChange={handleChange}
            placeholder="Min. 8 characters"
            className={`w-full h-11 pl-10 pr-11 rounded-xl bg-white/5 border text-white placeholder-slate-500 text-sm font-body focus:outline-none focus:ring-2 focus:ring-teal-500/50 transition-all ${
              errors?.password ? "border-red-500/60" : "border-white/10 focus:border-teal-500/50"
            }`}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
          >
            <Icon name={showPassword ? "EyeOff" : "Eye"} size={16} />
          </button>
        </div>
        <PasswordStrength password={formData?.password} />
        {errors?.password && <p className="text-xs text-red-400 font-caption">{errors?.password}</p>}
      </div>
      {/* Confirm Password */}
      <div className="space-y-1.5">
        <label className="text-sm font-medium text-slate-300 font-caption">Confirm Password *</label>
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
            <Icon name="ShieldCheck" size={16} />
          </div>
          <input
            type={showConfirmPassword ? "text" : "password"}
            name="confirmPassword"
            value={formData?.confirmPassword}
            onChange={handleChange}
            placeholder="Re-enter your password"
            className={`w-full h-11 pl-10 pr-11 rounded-xl bg-white/5 border text-white placeholder-slate-500 text-sm font-body focus:outline-none focus:ring-2 focus:ring-teal-500/50 transition-all ${
              errors?.confirmPassword ? "border-red-500/60" : "border-white/10 focus:border-teal-500/50"
            }`}
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
          >
            <Icon name={showConfirmPassword ? "EyeOff" : "Eye"} size={16} />
          </button>
        </div>
        {formData?.confirmPassword && formData?.password === formData?.confirmPassword && (
          <p className="text-xs text-emerald-400 font-caption flex items-center gap-1">
            <Icon name="Check" size={12} /> Passwords match
          </p>
        )}
        {errors?.confirmPassword && <p className="text-xs text-red-400 font-caption">{errors?.confirmPassword}</p>}
      </div>
      {/* Terms & Privacy */}
      <div className="space-y-3 pt-1">
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id="agreeTerms"
            name="agreeTerms"
            checked={formData?.agreeTerms}
            onChange={handleChange}
            className="w-4 h-4 mt-0.5 rounded border-white/20 bg-white/5 text-teal-500 focus:ring-teal-500/50 focus:ring-offset-0 flex-shrink-0"
          />
          <label htmlFor="agreeTerms" className="text-sm text-slate-400 font-body cursor-pointer leading-relaxed">
            I agree to the{" "}
            <button
              type="button"
              onClick={onShowTerms}
              className="text-teal-400 hover:text-teal-300 underline transition-colors"
            >
              Terms & Conditions
            </button>
          </label>
        </div>
        {errors?.agreeTerms && <p className="text-xs text-red-400 font-caption ml-7">{errors?.agreeTerms}</p>}

        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id="agreePrivacy"
            name="agreePrivacy"
            checked={formData?.agreePrivacy}
            onChange={handleChange}
            className="w-4 h-4 mt-0.5 rounded border-white/20 bg-white/5 text-teal-500 focus:ring-teal-500/50 focus:ring-offset-0 flex-shrink-0"
          />
          <label htmlFor="agreePrivacy" className="text-sm text-slate-400 font-body cursor-pointer leading-relaxed">
            I acknowledge the{" "}
            <span className="text-teal-400">Privacy Policy</span>{" "}
            and consent to data processing
          </label>
        </div>
        {errors?.agreePrivacy && <p className="text-xs text-red-400 font-caption ml-7">{errors?.agreePrivacy}</p>}
      </div>
      {/* Submit */}
      <button
        type="submit"
        disabled={isLoading}
        className="w-full h-11 rounded-xl bg-primary hover:bg-primary/90 text-white font-semibold text-sm font-caption transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-teal-900/30 mt-2"
      >
        {isLoading ? (
          <>
            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Creating account...
          </>
        ) : (
          <>
            Create Account
            <Icon name="ArrowRight" size={16} />
          </>
        )}
      </button>
    </form>
  );
}
